import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useIntuneQuery, intuneKeys } from '../hooks/useIntuneQuery'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  TrashIcon,
  PencilIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  MinusCircleIcon,
  CloudArrowUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline'
import {
  getDeploymentDetails,
  getDeploymentDeviceStatus,
  listAssignments,
  redeployToIntune,
  deleteDeployment,
  updateAppInformation,
  type AppInformationFields,
  listIntuneAppCategories,
  updateAdvanced,
  updateExperience,
  updateSubscription,
  updateDeploymentPolicy,
  type UpdateMode,
  type DeploymentTemplate,
  checkSubscription,
  syncSubscription,
  getTenantAppAutoUpdate,
  retryDeploymentSync,
  makeDeploymentAppsIdentical,
  recreateUpdateApp,
} from '../services/api'
import { taskKeys } from '../hooks/useTaskList'
import type { Assignment, DeploymentDetails, DeploymentsListResponse, PublishedVersion } from '../services/api'
import {
  Badge,
  SectionCard,
  EmptyState,
  Modal,
  Spinner,
  StatCard,
  SyncStatus,
  Tabs,
  Toggle,
} from '../components/ui'
import { uxLine } from '../utils/templateSummary'
import { plural } from '../utils/plural'
import { formatArchitectures, formatSettingValue } from '../utils/settingValues'
import { deploymentTitle } from '../utils/deploymentRows'
import AssignmentBlade, {
  assignmentSummary,
  type AssignmentGroup,
  type Intent,
} from '../components/AssignmentBlade'
import { assignmentsToRows } from '../utils/assignmentPayload'
import AssignmentModal from '../components/AssignmentModal'
import SoakDaysInput, {
  keepOptionsIncluding,
  soakDaysError,
  soakDaysValue,
} from '../components/SoakDaysInput'
import DeviceInstallStatusTable from '../components/DeviceInstallStatusTable'
import UxTemplateWizard from '../components/UxTemplateWizard'
import type { PreviewProcess, PreviewSampleApp } from '../components/PsadtDialogPreview'
import AdvancedSettings, {
  hardwareSummary,
  emptyRequirements,
  defaultReturnCodes,
  DEFAULT_RESTART_BEHAVIOUR,
  type IntuneRequirements,
  type AdvancedProgram,
  type Arch,
  type RestartBehaviour,
} from '../components/AdvancedSettings'
import { errorText } from '../utils/errorText'
import CommandBlock from '../components/CommandBlock'
import { GroupsNote, RelatedAppList, RelationshipNotes, RemovalPreviewStatus, RemoveScopeChoice, removalBlocked, removalTarget, useRemovalPreview, type RemoveScope } from '../components/RemovalPreview'
import { dateTime } from '../utils/date'

/**
 * ONE deployment — the mock's app/(console)/deployments/[id]/page.tsx.
 *
 * This page exists to give the per-ROLLOUT settings a home. The catalog page is about the
 * package; a deployment is what that package became when it went out, and the same package can
 * follow new versions on one rollout and stay pinned on another (spec §11.3b). Everything that
 * was homeless while there was no such page lands here.
 *
 * It replaced the Details MODAL on the deployments list. A modal was the wrong container: this
 * has its own actions, its own sub-dialogs (assign, edit uninstall) and deserves a URL you can
 * link a colleague to.
 *
 * One page per deployment: the install app and the update-only app of a deployment open the same
 * page (the update-only app's URL is replaced by the install app's). Every Edit writes both apps;
 * an app an edit did not reach is listed with a Retry, and the shared settings the two apps
 * disagree on are listed with Make identical.
 *
 * Honestly NOT here, because the data does not exist rather than because it was forgotten:
 *   - A rollout-specific package download. /apps/{id}/download-package takes (app_id, method)
 *     and resolves the app's single package_dir — there is no deployment or version parameter,
 *     so the only file it can serve is the catalog's current build. "View in Catalog" goes to the
 *     page where that is what it says it is.
 */

/** Flatten the decoded deployment config into label/value pairs, deepest keys dotted.
 *  A settings object rendered as raw JSON is technically complete and practically unreadable; the
 *  point of decoding it was to make the parameters legible, so it is shown as rows. Values are
 *  stringified rather than rendered structurally because these are leaf settings — modes, counts,
 *  booleans, timeouts — and an array here is a short list of app names, not a tree. */
function flattenConfig(cfg: Record<string, any>, prefix = ''): [string, string][] {
  const out: [string, string][] = []
  for (const [k, v] of Object.entries(cfg || {})) {
    const key = prefix ? `${prefix}.${k}` : k
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      out.push(...flattenConfig(v, key))
    } else if (Array.isArray(v)) {
      out.push([key, v.length ? v.join(', ') : '—'])
    } else if (typeof v === 'boolean') {
      out.push([key, v ? 'Yes' : 'No'])
    } else {
      out.push([key, v === null || v === '' ? '—' : String(v)])
    }
  }
  return out
}

const btnOutline =
  'inline-flex items-center gap-1.5 rounded-lg border border-theme-border-primary px-3 py-1.5 ' +
  'text-sm text-theme-text-primary hover:bg-theme-tertiary disabled:opacity-50'
const btnDanger =
  'inline-flex items-center gap-1.5 rounded-lg border border-theme-border-primary px-3 py-1.5 ' +
  'text-sm text-theme-text-secondary hover:border-red-500/40 hover:text-red-500 disabled:opacity-50'

/** The groups named by a set of assignments, as the blade's picker shape.
 *
 *  The blade resolves a row's group by id against this list. Entra is not queried here — a
 *  read-only card must not depend on a directory read the page does not otherwise need — so the
 *  names come from the assignments themselves. Membership is unknown and stated as such rather
 *  than guessed: the read-only blade never uses it to decide anything. */
function groupsFromAssignments(list: Assignment[]): AssignmentGroup[] {
  const out = new Map<string, AssignmentGroup>()
  for (const a of list) {
    if (a.target.type !== 'group' || !a.target.id) continue
    out.set(a.target.id, {
      id: a.target.id,
      name: a.target.name || a.target.id,
      membershipType: 'assigned',
      memberType: 'mixed',
    })
  }
  return [...out.values()]
}

/**
 * The intents this deployment's own app object can carry. An update-only app patches devices that
 * already have the app, so Available is never one of them. Uninstall stays only when there is no
 * install app beside it — a deployment published without one — because that app is then the
 * only object that can remove the software.
 */
function appAssignmentIntents(
  dep: Pick<DeploymentDetails, 'role' | 'parent_intune_app_id'>,
): Intent[] {
  if (dep.role !== 'update_only') return ['required', 'available', 'uninstall']
  return dep.parent_intune_app_id ? ['required'] : ['required', 'uninstall']
}

/**
 * The app the user-experience previews draw: this deployment's own name, version, icon and, when
 * the shipped configuration lists them, the processes its close-apps dialog names.
 */
function previewSampleApp(dep: DeploymentDetails): PreviewSampleApp {
  const src: any = dep.deployment_config_source ?? {}
  const flat: any = dep.deployment_config ?? {}
  const raw: unknown =
    src.closeApps?.processes ?? src.closeProcesses ?? flat.closeProcesses ?? []
  const processes: PreviewProcess[] = (Array.isArray(raw) ? raw : [])
    .map((p: any): PreviewProcess | null => {
      if (typeof p === 'string') {
        const exe = p.trim()
        if (!exe) return null
        return { name: exe.replace(/\.exe$/i, ''), exe: /\.exe$/i.test(exe) ? exe : `${exe}.exe` }
      }
      const exe = p?.processName ?? p?.name ?? p?.exe
      if (typeof exe !== 'string' || !exe.trim()) return null
      const name = p?.processDescription ?? p?.description ?? exe.replace(/\.exe$/i, '')
      return { name, exe: /\.exe$/i.test(exe) ? exe : `${exe}.exe` }
    })
    .filter((p): p is PreviewProcess => p !== null)
  return {
    // The name the dialog on the device shows: the app's, not the Intune app's.
    title: [dep.catalog_info?.display_name || dep.app_name, dep.version].filter(Boolean).join(' '),
    processes,
    icon: dep.icon_url || dep.catalog_info?.icon_url || undefined,
  }
}

function Row({ label, value }: { label: string; value?: React.ReactNode }) {
  if (value === undefined || value === null || value === '') return null
  return (
    <div>
      <dt className="text-[12px] text-theme-text-tertiary">{label}</dt>
      <dd className="text-[13px] text-theme-text-primary break-words">{value}</dd>
    </div>
  )
}

/** What a deploy of this package keeps of an edit made here, said in each editor. */
const KEPT_WITH_DEPLOYMENT_TEMPLATE =
  "Kept when this deployment gets a new version, automatically or with Update, and when you deploy it again with the same deployment template. Choosing another deployment template, or discarding edits, applies the template's values."
const KEPT_WITH_UX_TEMPLATE =
  "Kept when this deployment gets a new version, automatically or with Update, and when you deploy it again with the same user experience template. Choosing another user experience template, or discarding edits, applies the template's values."

/** What a save says it did: on both apps, or on one when the other missed it (the missed edit is
 *  listed on the page with Retry). */
function savedNotice(what: string, bothApps: boolean, missed?: string[]): string {
  if (missed?.length) return `${what} updated on one app in Intune; the other app missed it.`
  return bothApps ? `${what} updated on both apps in Intune.` : `${what} updated in Intune.`
}

/** Writes a saved update policy into the cached deployment, so the card shows it at once. */
function withPolicy(res: {
  follow_new_versions: boolean
  soak_days: number
  keep_versions: number
  uninstall_previous: boolean
  update_policy_edited: string[]
}) {
  return (d: DeploymentDetails): DeploymentDetails => ({
    ...d,
    follow_new_versions: res.follow_new_versions,
    soak_days: res.soak_days,
    keep_versions: res.keep_versions,
    uninstall_previous: res.uninstall_previous,
    update_policy_edited: res.update_policy_edited,
    automated_updates_effective: {
      ...(d.automated_updates_effective ?? {}),
      followNewVersions: res.follow_new_versions,
      soakDays: res.soak_days,
      keepVersions: res.keep_versions,
      uninstallPrevious: res.uninstall_previous,
    },
  })
}

/** How often the page asks again while the install status report is being prepared. */
const DEVICE_STATUS_POLL_MS = 5_000

/** Said in each editor of a deployment with both apps. */
const BOTH_APPS =
  'Saved on both apps of this deployment: the install app and the update-only app.'

/** Said in a shared settings card on the Update app tab: the card reads the install app, and an
 *  edit is saved on both apps. */
function SharedNote({ what = 'These settings are' }: { what?: string }) {
  return (
    <p className="mb-3 rounded-lg border border-theme-border-primary bg-theme-tertiary px-3 py-2 text-[12px] text-theme-text-secondary">
      {what} shared by both apps of this deployment: an edit is saved on the install app and the
      update app.
    </p>
  )
}

/** The script text of a Graph win32LobAppPowerShellScriptRule, decoded from its base64
 *  scriptContent; null when it cannot be decoded. */
function decodedScript(rule: any): string | null {
  if (typeof rule?.scriptContent !== 'string') return null
  try {
    const bytes = Uint8Array.from(atob(rule.scriptContent), (c) => c.charCodeAt(0))
    return new TextDecoder().decode(bytes)
  } catch {
    return null
  }
}

/** The update app's own facts, shown at the top of the Update app tab: its Intune name and the
 *  requirement script that limits it to devices that already have the app. */
function UpdateAppPanel({ name, rules }: { name: string; rules: any[] | null }) {
  const scripts = (rules ?? []).filter((r) =>
    String(r?.['@odata.type'] ?? '').includes('PowerShellScript'))
  return (
    <div className="mb-6 rounded-xl border border-theme-border-primary bg-theme-secondary px-5 py-4 text-sm">
      <p className="text-[12px] text-theme-text-tertiary">Update app in Intune</p>
      <p className="font-medium text-theme-text-primary">{name}</p>
      <p className="mt-1 text-[12px] text-theme-text-secondary">
        It updates devices that already have the app and installs nothing new. Its assignment
        is Required only.
      </p>
      <p className="mt-3 text-[12px] text-theme-text-tertiary">Requirement script</p>
      {rules === null ? (
        <p className="text-[12px] text-theme-text-secondary">Could not be read from Intune.</p>
      ) : scripts.length === 0 ? (
        <p className="text-[12px] text-theme-text-secondary">None</p>
      ) : (
        <ul className="space-y-2">
          {scripts.map((r, i) => {
            const text = decodedScript(r)
            return (
              <li key={`${r.displayName ?? 'script'}-${i}`}>
                <p className="text-[13px] text-theme-text-primary">
                  {r.displayName || 'Requirement script'}
                  <span className="text-theme-text-tertiary">
                    {' '}· runs as {r.runAsAccount || 'system'}; the app installs only where it
                    returns {String(r.comparisonValue ?? 'true')}
                  </span>
                </p>
                {text && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-[12px] text-theme-accent-blue">Show script</summary>
                    <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap rounded-lg bg-theme-tertiary p-2 font-mono text-[11px] text-theme-text-primary">
                      {text}
                    </pre>
                  </details>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

/** The deployment-page sections an edit belongs to, as a missed edit names them. */
const SECTION_LABEL: Record<string, string> = {
  app_information: 'An app information edit',
  advanced: 'An advanced settings edit',
  experience: 'A user experience edit',
  install_command: 'An install command edit',
  uninstall_command: 'An uninstall command edit',
}

/** A section's Edit button, with "Edited" when the section was changed on this page. */
function EditAction({
  edited,
  onClick,
  kept = KEPT_WITH_DEPLOYMENT_TEMPLATE,
}: {
  edited: boolean
  onClick: () => void
  /** When a deploy keeps the edit, shown on the badge. */
  kept?: string
}) {
  return (
    <span className="flex items-center gap-2">
      {edited && (
        <span title={kept}>
          <Badge tone="amber">Edited</Badge>
        </span>
      )}
      <button
        type="button"
        className="inline-flex items-center gap-1 text-[12px] font-medium text-theme-accent-blue hover:underline"
        onClick={onClick}
      >
        <PencilIcon className="h-3 w-3" /> Edit
      </button>
    </span>
  )
}

/** A label/value pair on one line, the way the settings cards read them out. `Row` stacks its two
 *  halves and is right for a dense grid; a card of six settings wants the value on the right. */
function LineRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-theme-text-secondary">{label}</dt>
      <dd className="text-right font-medium text-theme-text-primary">{children}</dd>
    </div>
  )
}

/** Every publish of this package, newest first.
 *
 *  A version whose Intune app object retention has since deleted still shipped, so it stays in the
 *  list and is marked as gone — dropping it would make the history disagree with itself. An older
 *  version Intune still has is marked Superseded. `inIntune`
 *  is null when the check could not run at all, which is neither "here" nor "gone". */
function VersionHistory({ versions }: { versions: PublishedVersion[] }) {
  const [showAll, setShowAll] = useState(false)
  const stillHere = versions.filter((v) => v.inIntune).length
  const checked = versions.some((v) => v.inIntune !== null)
  const shown = showAll ? versions : versions.slice(0, 3)
  const hidden = versions.length - shown.length

  return (
    <div className="mt-4 border-t border-theme-border-primary pt-3">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-[12px] font-semibold uppercase tracking-wide text-theme-text-secondary">
          Published to Intune
        </p>
        <p className="text-[12px] text-theme-text-tertiary">
          {versions.length === 10 ? 'Last 10 versions' : plural(versions.length, 'version')}
          {checked && ` · ${stillHere} still in the tenant`}
        </p>
      </div>
      <ul className="divide-y divide-theme-border-primary">
        {shown.map((v) => (
          <li
            key={`${v.intuneAppId ?? v.version}`}
            className="flex items-center justify-between gap-4 py-2 text-sm"
          >
            <span className="flex min-w-0 items-center gap-2">
              <span className="font-mono text-[13px] text-theme-text-primary">
                {v.version || '—'}
              </span>
              {versions[0]?.intuneAppId === v.intuneAppId && <Badge tone="green">Current</Badge>}
            </span>
            <span className="flex shrink-0 items-center gap-3">
              <span className="text-[12px] text-theme-text-secondary">
                {v.publishedAt ? new Date(v.publishedAt).toLocaleDateString() : '—'}
              </span>
              {v.inIntune === null ? null : v.inIntune ? (
                versions[0]?.intuneAppId === v.intuneAppId ? (
                  <Badge tone="blue">In Intune</Badge>
                ) : (
                  <span title="Still in Intune; a newer version supersedes it">
                    <Badge tone="slate">Superseded</Badge>
                  </span>
                )
              ) : (
                <span className="text-[12px] text-theme-text-tertiary">Removed</span>
              )}
            </span>
          </li>
        ))}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-theme-accent-blue hover:underline"
        >
          <ChevronDownIcon className="h-3.5 w-3.5" />
          Show {hidden} older {hidden === 1 ? 'version' : 'versions'}
        </button>
      )}
      {showAll && versions.length > 3 && (
        <button
          type="button"
          onClick={() => setShowAll(false)}
          className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-theme-accent-blue hover:underline"
        >
          <ChevronDownIcon className="h-3.5 w-3.5 rotate-180" />
          Show fewer
        </button>
      )}
    </div>
  )
}

/** Where the automatic update of this deployment stands. */
function UpdateState({ dep }: { dep: DeploymentDetails }) {
  const to = dep.update_to_version
  const when = dep.update_ready_at ? dateTime(dep.update_ready_at) : null
  const line: Record<string, [string, string] | null> = {
    up_to_date: null,
    waiting: ['blue', `Version ${to} publishes to this deployment${when ? ` on ${when}` : ' after its wait'}.`],
    due: ['blue', `Version ${to} publishes to this deployment at the next automatic update.`],
    job_open: ['blue', `Version ${to} waits for the deployment of this package that is running.`],
    failed: ['red', `The automatic update to ${to} failed${when ? `; it is tried again on ${when}` : ''}. ${dep.update_reason ?? ''}`],
    not_following: ['amber', `Version ${to} is available. This deployment does not follow new versions; deploy it to update.`],
    held: ['amber', `Version ${to} is available. The package is pinned.`],
    cannot_rebuild: ['amber', `Version ${to} is available, but this deployment cannot be updated automatically: ${dep.update_reason}. Deploy it to update.`],
    objects_differ: ['amber', `Version ${to} is available, but this deployment cannot be updated automatically: ${dep.update_reason}. Deploy it to update.`],
    removal_pending: ['amber', `Version ${to} is available; this deployment is being removed.`],
  }
  const shown = line[dep.update_state ?? ''] ?? null
  if (!shown) return null
  const [tone, text] = shown
  const box = {
    blue: 'border-blue-500/30 bg-blue-500/[0.08]',
    amber: 'border-amber-500/30 bg-amber-500/[0.07]',
    red: 'border-red-500/30 bg-red-500/[0.07]',
  }[tone]
  return (
    <p className={`mt-4 flex items-start gap-2 rounded-lg border px-3 py-2.5 text-[12px] text-theme-text-primary ${box}`}>
      <ClockIcon className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{text}</span>
    </p>
  )
}

/** A newer version is in the catalog and this rollout is not on it yet.
 *
 *  Which of the two notices applies is the whole point of the update mode: following, it publishes
 *  itself and this only says so; not following, nothing happens at all until someone approves — and
 *  an app silently sitting on an old version is exactly what that reads like otherwise. */
function PendingPublish({
  version,
  follows,
  soakDays,
  onApprove,
  approving,
}: {
  version: string
  follows: boolean
  soakDays: number
  onApprove: () => void
  approving: boolean
}) {
  if (follows) {
    return (
      <div className="mt-4 rounded-lg border border-blue-500/30 bg-blue-500/[0.08] px-3 py-2.5">
        <p className="flex items-center gap-2 text-sm font-medium text-theme-text-primary">
          <ClockIcon className="h-4 w-4 shrink-0 text-theme-accent-blue" />
          Version {version}{' '}
          {soakDays === 0
            ? 'publishes on the next sync'
            : `publishes ${plural(soakDays, 'day')} after the vendor release`}
        </p>
        {soakDays > 0 && (
          <p className="mt-1 text-[12px] text-theme-text-secondary">
            Waiting a few days after a vendor release lets a bad release surface before it
            reaches your estate.
          </p>
        )}
      </div>
    )
  }
  return (
    <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-medium text-amber-500">
          <ClockIcon className="h-4 w-4 shrink-0" />
          Version {version} is waiting
        </p>
        <button type="button" className={btnOutline} onClick={onApprove} disabled={approving}>
          {approving ? 'Publishing…' : 'Approve and publish'}
        </button>
      </div>
      <p className="mt-1 text-[12px] text-theme-text-secondary">
        Nothing publishes on its own while this app is not following new versions — it stays on the
        deployed version until you approve.
      </p>
    </div>
  )
}

export default function DeploymentDetail() {
  const { deploymentId } = useParams<{ deploymentId: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()

  /** Which app object the assignment editor opens on, or null when it is closed. */
  const [assignOpen, setAssignOpen] = useState<'app' | 'updateOnly' | null>(null)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [confirmRedeploy, setConfirmRedeploy] = useState(false)
  const [editAppInfo, setEditAppInfo] = useState(false)
  const [appInfoDraft, setAppInfoDraft] = useState<Required<AppInformationFields>>({
    owner: '', developer: '', notes: '', information_url: '', privacy_url: '',
    is_featured: false, allow_uninstall: false, categories: [],
  })
  const [editAdvanced, setEditAdvanced] = useState(false)
  const [advancedReq, setAdvancedReq] = useState<IntuneRequirements>(emptyRequirements)
  const [advancedProg, setAdvancedProg] = useState<AdvancedProgram>({
    restartBehaviour: DEFAULT_RESTART_BEHAVIOUR, returnCodes: defaultReturnCodes(),
  })
  const [editExperience, setEditExperience] = useState(false)
  const [editUpdates, setEditUpdates] = useState(false)
  type UpdatesDraft = {
    update_mode: UpdateMode; pinned_version: string
    soak_days: number; keep_versions: number; uninstall_previous: boolean
  }
  const [updatesDraft, setUpdatesDraft] = useState<UpdatesDraft>(
    { update_mode: 'FOLLOW', pinned_version: '', soak_days: 0, keep_versions: 3, uninstall_previous: false })
  /** The policy the editor opened on. Save sends only what differs from it, so a setting the admin
   *  did not touch stays NULL on the subscription and keeps following the template. */
  const [updatesSeed, setUpdatesSeed] = useState<UpdatesDraft | null>(null)
  const [soakText, setSoakText] = useState('0')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  /** What Intune did not accept on a save that otherwise succeeded. */
  const [warnings, setWarnings] = useState<string[]>([])
  /** When the last save landed, so the page can say it is re-reading Intune until data newer
   *  than that save arrives. */
  const [savedAt, setSavedAt] = useState<number | null>(null)
  /** Every action starts from a clean slate: a message from an earlier action describes that
   *  action, and left on screen it reads as the result of this one. */
  const clearMessages = () => {
    setError(null)
    setNotice(null)
    setWarnings([])
  }

  const depQ = useIntuneQuery({
    queryKey: intuneKeys.deploymentDetails(deploymentId),
    queryFn: () => getDeploymentDetails(deploymentId!),
    enabled: !!deploymentId,
    retry: false,
  })
  const dep = depQ.data
  /** The deployment has an install app and an update-only app; every edit writes both. */
  const hasSiblingApp = !!(dep?.update_only_app_id || dep?.parent_intune_app_id)

  // One page per deployment: the update-only app's URL becomes the install app's.
  useEffect(() => {
    if (dep?.primary_app_id && deploymentId && dep.primary_app_id !== deploymentId) {
      navigate(`/deployments/${dep.primary_app_id}`, { replace: true })
    }
  }, [dep?.primary_app_id, deploymentId, navigate])
  const edited = (section: 'experience' | 'app_information' | 'advanced' | 'assignments') =>
    !!dep?.overrides?.[section]

  /**
   * Writes what was just saved into the cached deployment at once, so the cards report the new
   * value while Intune is re-read; the invalidate that follows replaces it with Intune's answer.
   * The prefix key matches whatever tenant suffix useIntuneQuery appends.
   */
  const applySaved = (update: (d: DeploymentDetails) => DeploymentDetails) => {
    qc.setQueriesData<DeploymentDetails>(
      { queryKey: intuneKeys.deploymentDetails(deploymentId) },
      (old) => (old ? update(old) : old),
    )
    setSavedAt(Date.now())
    qc.invalidateQueries({ queryKey: intuneKeys.deploymentDetails(deploymentId) })
  }

  const assignQ = useIntuneQuery({
    queryKey: intuneKeys.assignments(deploymentId),
    queryFn: () => listAssignments(deploymentId!),
    enabled: !!deploymentId,
    retry: false,
  })

  // The update-only twin's assignments live on ITS app object, so they are a second read. Without
  // it the card could only ever report that the twin exists, never who it reaches.
  const updateOnlyId = dep?.update_only_app_id ?? null
  const updateOnlyQ = useIntuneQuery({
    queryKey: intuneKeys.assignments(updateOnlyId),
    queryFn: () => listAssignments(updateOnlyId!),
    enabled: !!updateOnlyId,
    retry: false,
  })

  // The per-rollout update story. Both of these are keyed on the APP, which is the honest shape of
  // what we have: our subscription and auto-update settings are per app-per-tenant, not per
  // deployment. The mock's model is per deployment; saying so here beats implying otherwise.
  // Install status runs Intune's report export, which takes far longer than everything else on
  // this page, so it is its own request and its own section-level spinner.
  /** Which app of the deployment the page shows: the install app or the update-only app. It
   *  drives the status counts, the device table, the Assignment card and its editor. */
  const [appTab, setAppTab] = useState<'install' | 'update'>('install')
  /** The deployment has both apps, so the page offers the Install app and Update app tabs. */
  const hasUpdateTab = dep?.role !== 'update_only' && !!dep?.update_only_app_id
  const onUpdateTab = appTab === 'update' && hasUpdateTab
  const statusAppId = onUpdateTab ? dep!.update_only_app_id! : deploymentId
  /** The Update app tab of a deployment whose update app Intune no longer has: nothing on it
   *  can be read or edited in Intune. */
  const updateTabGone = onUpdateTab &&
    (dep?.apps ?? []).some((a) => a.role === 'update_only' && a.in_intune === false)
  const deviceStatusQ = useIntuneQuery({
    queryKey: intuneKeys.deviceStatus(statusAppId),
    queryFn: () => getDeploymentDeviceStatus(statusAppId!),
    enabled: !!statusAppId && !updateTabGone,
    retry: false,
    // While Intune's report export is still running the backend answers "preparing"; it is asked
    // again until the report is ready or has failed.
    refetchInterval: (q) => (q.state.data?.state === 'preparing' ? DEVICE_STATUS_POLL_MS : false),
  })
  const installStats = deviceStatusQ.data?.install_stats ?? null
  const statusPreparing = !updateTabGone &&
    (deviceStatusQ.isLoading || deviceStatusQ.data?.state === 'preparing')
  const statusFailed = !statusPreparing &&
    (deviceStatusQ.isError || deviceStatusQ.data?.state === 'error')
  const noDevices = !!installStats && (deviceStatusQ.data?.device_statuses ?? []).length === 0

  const appId = dep?.app_id ?? null
  const isCatalogApp = !!appId && !appId.startsWith('tenant-')
  const subQ = useQuery({
    queryKey: ['subscription', appId],
    queryFn: () => checkSubscription(appId!),
    enabled: !!appId && isCatalogApp,
    retry: false,
  })
  const autoQ = useQuery({
    queryKey: ['tenant-app-auto-update', appId],
    queryFn: () => getTenantAppAutoUpdate(appId!),
    enabled: !!appId && !isCatalogApp,
    retry: false,
  })

  const redeploy = useMutation({
    mutationFn: () => redeployToIntune(deploymentId!),
    onMutate: clearMessages,
    onSuccess: (res: any) => {
      setError(null)
      setNotice('Re-published to Intune.')
      setWarnings(res?.warnings ?? [])
      qc.invalidateQueries({ queryKey: intuneKeys.deploymentDetails(deploymentId) })
      qc.invalidateQueries({ queryKey: intuneKeys.deployments() })
    },
    onError: (e: any) => setError(errorText(e, 'Redeploy failed.')),
  })

  /** What Remove removes from a deployment with both apps. */
  const [removeScope, setRemoveScope] = useState<RemoveScope>('deployment')
  const removeTarget = deploymentId
    ? removalTarget(removeScope, deploymentId,
                    dep?.role !== 'update_only' ? dep?.update_only_app_id : null)
    : null
  const removalPreview = useRemovalPreview(
    removeTarget ? [removeTarget.id] : [], confirmRemove, removeTarget?.keepUpdateOnly ?? false)
  const removal = removalPreview.preview?.deployments[0]
  /** The Remove dialog names the deployment, as the list's bulk Remove does; the Intune app names
   *  are its details. */
  const removeTitle = dep
    ? deploymentTitle(dep.catalog_info?.display_name || dep.package_name || dep.app_name, dep.deployment_name)
    : ''
  const removesInstallAppOnly =
    removeScope === 'install' && dep?.role !== 'update_only' && !!dep?.update_only_app_id
  /** Set when the app was deleted but an older version of it was not: the dialog then reports
   *  that instead of asking for confirmation. */
  const [removeIncomplete, setRemoveIncomplete] = useState<string | null>(null)

  const remove = useMutation({
    mutationFn: () => deleteDeployment(removeTarget!.id, removeTarget!.keepUpdateOnly),
    onMutate: clearMessages,
    onSuccess: (res) => {
      setError(null)
      qc.invalidateQueries({ queryKey: intuneKeys.deployments() })
      if (res.status === 'partial') {
        setRemoveIncomplete(res.detail || res.message)
        return
      }
      if (removeScope === 'update') {
        // The deployment stays, with its install app.
        setConfirmRemove(false)
        setNotice('The update-only app was removed from Intune. The install app is not changed.')
        qc.invalidateQueries({ queryKey: intuneKeys.deploymentDetails(deploymentId) })
        return
      }
      // Back to the list: this page's subject no longer exists. Its rows leave the cached list
      // first, so the list never shows them as Not in Intune while its next read is on the way.
      const gone = new Set([removeTarget!.id, ...(removal?.related ?? []).map((r) => r.intune_app_id)])
      qc.setQueriesData<DeploymentsListResponse>({ queryKey: intuneKeys.deployments() }, (list) =>
        list && { ...list, deployments: list.deployments.filter((d) => !gone.has(d.id)) })
      // Setting the data marks it fresh; it is marked stale again so the list reads it anew.
      qc.invalidateQueries({ queryKey: intuneKeys.deployments() })
      navigate('/deployments')
    },
    onError: (e: any) => {
      setConfirmRemove(false)
      setError(errorText(e, 'Remove failed.'))
    },
  })

  const sync = useMutation({
    mutationFn: (subId: string) => syncSubscription(subId),
    onMutate: clearMessages,
    onSuccess: () => {
      setError(null)
      setNotice('Sync requested.')
      qc.invalidateQueries({ queryKey: ['subscription', appId] })
    },
    onError: (e: any) => setError(errorText(e, 'Sync failed.')),
  })

  const catsQ = useIntuneQuery({
    queryKey: intuneKeys.appCategories(),
    queryFn: listIntuneAppCategories,
    enabled: editAppInfo,
  })

  /** The deployment with the app information `saved` written in. `uninstallKept` is the
   *  Company Portal uninstall setting Intune kept when it refused the new one. */
  const withAppInfo =
    (saved: Required<AppInformationFields>, uninstallKept?: boolean) =>
    (d: DeploymentDetails): DeploymentDetails => ({
      ...d,
      overrides: { ...(d.overrides ?? {}), app_information: d.overrides?.app_information ?? {} },
      intune_metadata: {
        ...(d.intune_metadata ?? {}),
        owner: saved.owner,
        developer: saved.developer,
        notes: saved.notes,
        informationUrl: saved.information_url,
        privacyUrl: saved.privacy_url,
        featured: saved.is_featured,
        categories: saved.categories,
      },
      allow_uninstall_from_company_portal: uninstallKept ?? saved.allow_uninstall,
    })

  /* Writing to Intune takes several seconds. The section shows the new values as soon as Save is
     pressed, marked as being saved, and goes back to the previous values if Intune refuses. */
  const saveAppInfo = useMutation({
    mutationFn: (draft: Required<AppInformationFields>) => updateAppInformation(deploymentId!, draft),
    onMutate: async (draft) => {
      clearMessages()
      const key = { queryKey: intuneKeys.deploymentDetails(deploymentId) }
      await qc.cancelQueries(key)
      const previous = qc.getQueriesData<DeploymentDetails>(key)
      qc.setQueriesData<DeploymentDetails>(key, (old) => (old ? withAppInfo(draft)(old) : old))
      setEditAppInfo(false)
      return { previous }
    },
    onSuccess: (res, draft, context) => {
      setError(null)
      setNotice(savedNotice('App information', hasSiblingApp, res?.out_of_sync))
      setWarnings(res?.warnings ?? [])
      // A warning naming allowAvailableUninstall means Intune kept its old value.
      const uninstallRefused = (res?.warnings ?? []).some((w) => w.includes('allowAvailableUninstall'))
      const before = context?.previous.find(([, data]) => data)?.[1]
      applySaved(
        withAppInfo(
          draft,
          uninstallRefused ? !!before?.allow_uninstall_from_company_portal : undefined,
        ),
      )
    },
    onError: (e: any, _draft, context) => {
      context?.previous.forEach(([queryKey, data]) => qc.setQueryData(queryKey, data))
      setError(`${errorText(e, 'Could not save.')} The app information was not changed.`)
      // The dialog opens again with what was typed, so the admin can correct it and retry.
      setEditAppInfo(true)
    },
  })

  const saveAdvanced = useMutation({
    mutationFn: () => updateAdvanced(deploymentId!, {
      minimum_os: advancedReq.minimumOs || undefined,
      disk_space_mb: advancedReq.diskSpaceMb ?? undefined,
      memory_mb: advancedReq.memoryMb ?? undefined,
      logical_processors: advancedReq.logicalProcessors ?? undefined,
      cpu_speed_mhz: advancedReq.cpuSpeedMhz ?? undefined,
      architectures: advancedReq.architectures.length ? advancedReq.architectures : undefined,
      return_codes: advancedProg.returnCodes,
      restart_behaviour: advancedProg.restartBehaviour,
      // Rules are sent only when the editor added some. Sending them replaces the app's other
      // requirement rules (the ones Agentic Packr manages are kept), so rules already there are
      // shown read-only and left out of the save.
      rules: advancedReq.rules.length ? advancedReq.rules : undefined,
    }),
    onMutate: clearMessages,
    onSuccess: (res) => {
      setEditAdvanced(false)
      setError(null)
      setNotice(savedNotice('Advanced settings', hasSiblingApp, res?.out_of_sync))
      setWarnings(res?.warnings ?? [])
      const req = advancedReq
      const prog = advancedProg
      // A warning naming allowedArchitectures means Intune kept its old architectures.
      const archRefused = (res?.warnings ?? []).some((w) => w.includes('allowedArchitectures'))
      applySaved((d) => ({
        ...d,
        overrides: { ...(d.overrides ?? {}), advanced: d.overrides?.advanced ?? {} },
        advanced: {
          ...(d.advanced ?? {}),
          minimumOs: req.minimumOs || d.advanced?.minimumOs,
          architectures: req.architectures.length && !archRefused
            ? req.architectures.join(',')
            : d.advanced?.architectures,
          minimumFreeDiskSpaceMB: req.diskSpaceMb ?? d.advanced?.minimumFreeDiskSpaceMB,
          minimumMemoryMB: req.memoryMb ?? d.advanced?.minimumMemoryMB,
          minimumProcessors: req.logicalProcessors ?? d.advanced?.minimumProcessors,
          minimumCpuSpeedMHz: req.cpuSpeedMhz ?? d.advanced?.minimumCpuSpeedMHz,
          returnCodes: prog.returnCodes,
          installExperience: {
            ...(d.advanced?.installExperience ?? {}),
            deviceRestartBehavior: prog.restartBehaviour,
          },
        },
      }))
    },
    onError: (e: any) => {
      setError(errorText(e, 'Could not save.'))
      // Part of the save may have reached Intune before the failure; show what it holds now.
      qc.invalidateQueries({ queryKey: intuneKeys.deploymentDetails(deploymentId) })
    },
  })

  const saveExperience = useMutation({
    mutationFn: (config: any) =>
      updateExperience(deploymentId!, { config }),
    onMutate: clearMessages,
    onSuccess: (res, config) => {
      setEditExperience(false)
      setError(null)
      setNotice(savedNotice('User experience', hasSiblingApp, res?.out_of_sync))
      setWarnings(res?.warnings ?? [])
      applySaved((d) => ({
        ...d,
        overrides: { ...(d.overrides ?? {}), experience: d.overrides?.experience ?? {} },
        deployment_config_source: config,
        ux_template: d.ux_template ? { ...d.ux_template, adjusted: true } : d.ux_template,
      }))
    },
    onError: (e: any) => setError(errorText(e, 'Could not save.')),
  })

  /** A saved update policy: shown on the card at once, and re-read wherever it is summarised —
   *  the package's subscription and the deployment list's Updates column. */
  const policySaved = (res: Parameters<typeof withPolicy>[0]) => {
    applySaved(withPolicy(res))
    qc.invalidateQueries({ queryKey: ['subscription', appId] })
    qc.invalidateQueries({ queryKey: intuneKeys.deployments() })
  }

  const saveUpdates = useMutation({
    mutationFn: () => {
      const follow = updatesDraft.update_mode === 'FOLLOW'
      // Only what changed. A copy of the template's own value would become a change of its own,
      // after which the template's value would no longer apply to this deployment.
      const changed = <K extends 'soak_days' | 'keep_versions' | 'uninstall_previous'>(key: K) =>
        follow && updatesDraft[key] !== updatesSeed?.[key] ? updatesDraft[key] : undefined
      return updateDeploymentPolicy(deploymentId!, {
        follow_new_versions:
          updatesDraft.update_mode !== updatesSeed?.update_mode ? follow : undefined,
        soak_days: changed('soak_days'),
        keep_versions: changed('keep_versions'),
        uninstall_previous: changed('uninstall_previous'),
      })
    },
    onMutate: clearMessages,
    onSuccess: (res) => {
      setEditUpdates(false)
      setError(null)
      setNotice('Update policy saved.')
      policySaved(res)
    },
    onError: (e: any) => setError(errorText(e, 'Could not save.')),
  })

  /** Drop this deployment's changed values back to its deployment template's. */
  const resetUpdates = useMutation({
    mutationFn: () =>
      updateDeploymentPolicy(deploymentId!, {
        clear: ['follow_new_versions', 'soak_days', 'keep_versions', 'uninstall_previous'],
      }),
    onMutate: clearMessages,
    onSuccess: (res) => {
      setError(null)
      setNotice("This deployment follows its deployment template's update policy again.")
      policySaved(res)
    },
    onError: (e: any) => setError(errorText(e, 'Could not reset the update policy.')),
  })

  /** Sends again every edit an app of the deployment missed. */
  const retrySync = useMutation({
    mutationFn: () => retryDeploymentSync(deploymentId!),
    onMutate: clearMessages,
    onSuccess: (res) => {
      if (res.in_sync) setNotice('Both apps of this deployment have every edit.')
      else setError(`Intune still refused: ${res.failed.map((f) => f.error).join('; ')}`)
      applySaved((d) => d)
    },
    onError: (e: any) => setError(errorText(e, 'Retry failed.')),
  })

  /** Copies the install app's shared settings to the update-only app. */
  const makeIdentical = useMutation({
    mutationFn: () => makeDeploymentAppsIdentical(deploymentId!),
    onMutate: clearMessages,
    onSuccess: (res) => {
      setWarnings(res.warnings ?? [])
      setNotice(res.remaining.length
        ? 'Some settings were copied to the update-only app; the rest are listed below.'
        : "The update-only app now has the install app's settings.")
      applySaved((d) => ({ ...d, app_differences: res.remaining }))
    },
    onError: (e: any) => setError(errorText(e, 'Could not make the apps identical.')),
  })

  /** Publishes the update app again, deleted in Intune, with this deployment's settings. */
  const recreate = useMutation({
    mutationFn: () => recreateUpdateApp(deploymentId!),
    onMutate: clearMessages,
    onSuccess: () => {
      setNotice(
        "The update app is being published again with this deployment's settings. Follow it " +
          'in Tasks; this page shows it when the deployment job ends.',
      )
      qc.invalidateQueries({ queryKey: taskKeys.deploymentJobs() })
    },
    onError: (e: any) => setError(errorText(e, 'Could not recreate the update app.')),
  })

  /** Releases a package pinned to a version: the pin holds every deployment of the package. */
  const unpin = useMutation({
    mutationFn: () => updateSubscription(sub!.id, { update_mode: 'FOLLOW' }),
    onMutate: clearMessages,
    onSuccess: () => {
      setError(null)
      setNotice('The package is no longer pinned.')
      qc.invalidateQueries({ queryKey: intuneKeys.deploymentDetails(deploymentId) })
      qc.invalidateQueries({ queryKey: ['subscription', appId] })
    },
    onError: (e: any) => setError(errorText(e, 'Could not unpin the package.')),
  })

  /** The Follow-new-versions switch on the card, which is the one setting worth reaching without
   *  opening the policy editor. It changes this deployment only. */
  const setFollowNewVersions = useMutation({
    mutationFn: (follow: boolean) =>
      updateDeploymentPolicy(deploymentId!, { follow_new_versions: follow }),
    onMutate: clearMessages,
    onSuccess: (res, follow) => {
      setError(null)
      setNotice(
        follow
          ? 'This deployment will follow new versions.'
          : 'This deployment stays on the published version.',
      )
      policySaved(res)
    },
    onError: (e: any) => setError(errorText(e, 'Could not change the update policy.')),
  })

  if (depQ.isLoading) {
    return <p className="p-8 text-center text-theme-text-tertiary">Loading…</p>
  }
  if (depQ.isError && !dep) {
    return (
      <div className="p-8 py-24 text-center">
        <p className="text-theme-text-tertiary">
          This deployment could not be read from Intune. {errorText(depQ.error)}
        </p>
        <button
          type="button"
          onClick={() => depQ.refetch()}
          className="mt-2 text-sm text-theme-accent-blue hover:underline"
        >
          Try again
        </button>
      </div>
    )
  }
  if (!dep) {
    return (
      <div className="p-8 py-24 text-center">
        <p className="text-theme-text-tertiary">Deployment not found.</p>
        <Link to="/deployments" className="mt-2 inline-block text-theme-accent-blue hover:underline">
          Back to deployments
        </Link>
      </div>
    )
  }

  /** Which app object the Assignment card reports and its Edit opens, following the app tab. */
  const assignTab: 'app' | 'updateOnly' = onUpdateTab ? 'updateOnly' : 'app'
  const updateApp = (dep.apps ?? []).find((a) => a.role === 'update_only')
  /** Intune answered that the update app is gone while the install app is still there. */
  const updateMissing = hasUpdateTab && updateApp?.in_intune === false && dep.in_intune !== false
  const assignments = assignQ.data?.assignments ?? []
  const updateOnlyAssignments = updateOnlyQ.data?.assignments ?? []
  const sub = subQ.data
  const auto = autoQ.data

  const appIntents = appAssignmentIntents(dep)
  const appRows = assignmentsToRows(assignments)
  const updateOnlyRows = assignmentsToRows(updateOnlyAssignments)
  // The blade names a group by looking it up in `groups`; the only group list this page has is the
  // one carried on the assignments themselves, so an unlisted group would read as "—".
  const appGroups = groupsFromAssignments(assignments)
  const updateOnlyGroups = groupsFromAssignments(updateOnlyAssignments)

  /* The automated-update policy IN FORCE, as the backend resolves it for the sync service: the
     deployment template this rollout was published from, as it stands now, under this
     subscription's overrides. Reading the subscription alone reports 0-day soak and 3 kept
     versions for every deployment that never overrode them; reading the published snapshot
     reports the policy as it was on publish day and hides every template edit since. */
  const publishedAuto = dep.automated_updates_published ?? {}
  const effectiveAuto = dep.automated_updates_effective ?? {}
  /* Whether this deployment follows new versions: its template's value or the one changed for it,
     as the backend resolves it (update_policy.deployment_policy). */
  const follows =
    dep.follow_new_versions ?? effectiveAuto.followNewVersions ?? !!publishedAuto.followNewVersions
  const pinned = sub?.update_mode === 'PIN'
  // A deployment other than the package's first.
  const namedDeployment = !!dep.deployment_uid
  // A new version does not reach it on its own: someone deploys it (a private app's other
  // deployments, or every other deployment while automatic updates act on the first only).
  const manualOnly = dep.updated_automatically === false
  const policyEdited = new Set(dep.update_policy_edited ?? [])
  const editedTag = (key: string) =>
    policyEdited.has(key) ? (
      <span className="ml-1.5 rounded bg-theme-tertiary px-1.5 py-0.5 text-[10px] text-theme-text-tertiary">
        changed for this deployment
      </span>
    ) : null
  const soakDays = effectiveAuto.soakDays ?? dep.soak_days ?? publishedAuto.soakDays ?? 7
  const keepVersions = effectiveAuto.keepVersions ?? dep.keep_versions ?? publishedAuto.keepVersions ?? 3
  const uninstallPrevious =
    effectiveAuto.uninstallPrevious ?? dep.uninstall_previous ?? publishedAuto.uninstallPrevious ?? false
  const supersede = effectiveAuto.supersede ?? publishedAuto.supersede ?? true

  const graphStamps = [depQ.dataUpdatedAt, assignQ.dataUpdatedAt].filter(Boolean)

  return (
    <div className="p-8">
      <div className="mx-auto max-w-4xl">
        <Link
          to="/deployments"
          className="mb-5 inline-flex items-center gap-2 text-sm font-medium text-theme-text-tertiary hover:text-theme-text-primary"
        >
          <ArrowLeftIcon className="h-4 w-4" /> Back to deployments
        </Link>

        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-4">
            {dep.icon_url || dep.catalog_info?.icon_url ? (
              <img
                src={dep.icon_url || dep.catalog_info?.icon_url}
                alt=""
                className="h-14 w-14 shrink-0 rounded-xl object-contain"
              />
            ) : (
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-theme-tertiary text-xl font-semibold text-theme-text-tertiary">
                {dep.app_name?.[0] ?? '?'}
              </div>
            )}
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                {/* The package and the deployment, as the deployment list names them; the first
                    deployment has no name and is the Default one. */}
                <h1 className="text-2xl font-bold tracking-tight text-theme-text-primary">
                  {deploymentTitle(dep.catalog_info?.display_name || dep.app_name, dep.deployment_name)}
                </h1>
                {/* Assigned means the app object carries an assignment, read off the live set
                    below — not its publishing state, which says nothing about who it reaches.
                    While that read is in flight the badge is held back rather than guessing. */}
                {dep.in_intune === false ? (
                  <span title="The app is no longer in Intune: it was deleted in the Intune admin center. The deployment record is kept, so Deploy publishes a new app and Remove drops the record.">
                    <Badge tone="red">Not in Intune</Badge>
                  </span>
                ) : dep.status === 'failed' ? (
                  <Badge tone="red">Failed</Badge>
                ) : dep.status === 'pending' ? (
                  <Badge tone="amber">Pending</Badge>
                ) : assignQ.isLoading ? null : assignQ.isError ? (
                  <Badge tone="slate">{dep.status}</Badge>
                ) : assignments.length > 0 ? (
                  <Badge tone="green">Assigned</Badge>
                ) : (
                  <Badge tone="slate">Not assigned</Badge>
                )}
                {updateMissing && (
                  <span title="The update app of this deployment was deleted in the Intune admin center. The install app is still there.">
                    <Badge tone="red">Update app missing in Intune</Badge>
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-theme-text-secondary">
                {[
                  dep.version,
                  dep.install_context === 'user' ? 'User' : dep.install_context === 'system' ? 'System' : null,
                  (dep.apps ?? []).length
                    ? (dep.apps ?? [])
                        .map((a) =>
                          `${a.role === 'update_only' ? 'Update app' : 'Install app'}${
                            a.in_intune === false ? ' (missing in Intune)' : ''}`)
                        .join(' + ')
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                {dep.in_catalog && dep.app_id && (
                  <>
                    {' · '}
                    <Link
                      to={`/apps/${dep.app_id}`}
                      className="text-theme-accent-blue hover:underline"
                    >
                      View in Catalog
                    </Link>
                  </>
                )}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <SyncStatus
              syncing={depQ.isFetching || assignQ.isFetching}
              updatedAt={graphStamps.length ? Math.min(...graphStamps) : 0}
              label="Syncing with Microsoft Intune…"
              className="mr-1"
            />
            {/* Sync leads, as it does on the reference: it is the read that makes everything below
                current, and it was previously buried inside the update-policy card. */}
            {sub && (
              <button
                type="button"
                className={btnOutline}
                onClick={() => sync.mutate(sub.id)}
                disabled={sync.isPending}
                title="Ask the catalog for a newer version of this package"
              >
                <ArrowPathIcon className={`h-4 w-4 ${sync.isPending ? 'animate-spin' : ''}`} />
                {sync.isPending ? 'Requesting…' : 'Sync'}
              </button>
            )}
            <button
              type="button"
              className={btnOutline}
              onClick={() => setAssignOpen(assignTab)}
              disabled={!dep.intune_app_id || dep.in_intune === false || (onUpdateTab && updateMissing)}
            >
              <PencilIcon className="h-4 w-4" /> Edit assignment
            </button>
            <button
              type="button"
              className={btnOutline}
              onClick={() => setConfirmRedeploy(true)}
              disabled={redeploy.isPending || dep.in_intune === false}
              title={
                dep.in_intune === false
                  ? 'The app is no longer in Intune. Deploy the package again from its catalog page.'
                  : 'Re-publish this package to Intune'
              }
            >
              <CloudArrowUpIcon className={`h-4 w-4 ${redeploy.isPending ? 'animate-pulse' : ''}`} />
              {redeploy.isPending ? 'Publishing…' : 'Redeploy'}
            </button>
            <button type="button" className={btnDanger} onClick={() => { setRemoveScope('deployment'); setConfirmRemove(true) }}>
              <TrashIcon className="h-4 w-4" /> Remove
            </button>
          </div>
        </div>

        {error && (
          <p className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-500">
            {error}
          </p>
        )}
        {notice && (
          <p className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-500">
            {notice}
          </p>
        )}
        {dep?.not_fully_tested && (
          <p className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-500">
            <span className="font-semibold">Not fully tested.</span> The deployed package was published
            without a passing test. Make sure the devices meet what its test could not.
          </p>
        )}
        {warnings.map((w) => (
          <p
            key={w}
            role="alert"
            className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-500"
          >
            {w}
          </p>
        ))}
        {updateMissing && (
          <div role="alert" className="mb-4 rounded-lg border border-red-500/30 bg-red-500/[0.07] px-4 py-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-medium text-theme-text-primary">Update app missing in Intune</p>
              <span className="flex flex-wrap gap-2">
                <button type="button" className={btnOutline} onClick={() => recreate.mutate()}
                  disabled={recreate.isPending}>
                  {recreate.isPending ? 'Starting…' : 'Recreate update app'}
                </button>
                <button type="button" className={btnDanger}
                  onClick={() => { setRemoveScope('update'); setConfirmRemove(true) }}>
                  Remove from deployment
                </button>
              </span>
            </div>
            <p className="mt-1 text-[12px] text-theme-text-secondary">
              {updateApp?.name ? `${updateApp.name} was` : 'The update app was'} deleted in the
              Intune admin center. The install app is still there. Recreate publishes the update
              app again with this deployment&apos;s settings; Remove from deployment drops it from
              this deployment and keeps the install app.
            </p>
          </div>
        )}
        {!!dep.missed_edits?.length && (
          <div role="alert" className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-medium text-amber-600 dark:text-amber-300">Out of sync</p>
              <button type="button" className={btnOutline} onClick={() => retrySync.mutate()}
                disabled={retrySync.isPending}>
                {retrySync.isPending ? 'Retrying…' : 'Retry'}
              </button>
            </div>
            <ul className="mt-1 space-y-0.5 text-[12px] text-theme-text-secondary">
              {dep.missed_edits.map((m, i) => (
                <li key={`${m.intune_app_id}-${i}`}>
                  {SECTION_LABEL[m.section] ?? m.section} did not reach the{' '}
                  {m.role === 'update_only' ? 'update-only app' : 'install app'}
                  {m.error ? `: ${m.error}` : '.'}
                </li>
              ))}
            </ul>
          </div>
        )}
        {!!dep.app_differences?.length && (
          <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-4 py-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-medium text-theme-text-primary">
                The install app and the update-only app differ
              </p>
              <button type="button" className={btnOutline} onClick={() => makeIdentical.mutate()}
                disabled={makeIdentical.isPending}
                title="Copies the install app's settings to the update-only app">
                {makeIdentical.isPending ? 'Copying…' : 'Make identical'}
              </button>
            </div>
            <p className="mt-1 text-[12px] text-theme-text-secondary">
              Both apps of a deployment should have the same settings. This happens when one app
              was changed in the Intune admin center, or edited before edits reached both apps.
              Make identical copies the install app&apos;s value to the update-only app.
            </p>
            <dl className="mt-2 space-y-1 text-[12px]">
              {dep.app_differences.map((d) => (
                <div key={d.field} className="flex flex-wrap justify-between gap-2 border-t border-amber-500/20 pt-1">
                  <dt className="text-theme-text-secondary">{d.label}</dt>
                  <dd className="text-right text-theme-text-primary">
                    {d.install === null && d.update === null
                      ? 'differs'
                      : `install app: ${String(d.install ?? '—')} · update-only app: ${String(d.update ?? '—')}`}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}
        {savedAt !== null && depQ.isFetching && depQ.dataUpdatedAt <= savedAt && (
          <p
            role="status"
            className="mb-4 flex items-center gap-2 text-[12px] text-theme-text-tertiary"
          >
            <Spinner className="h-3.5 w-3.5 shrink-0 text-theme-accent-blue" />
            Refreshing from Intune…
          </p>
        )}

        {/* The two Intune apps of the deployment: the tab picks which one the status, the
            assignment and the device table report. */}
        {hasUpdateTab && (
          <div className="mb-3 border-b border-theme-border-primary">
            <Tabs
              tabs={[
                { key: 'install', label: 'Install app' },
                { key: 'update', label: 'Update app' },
              ]}
              active={appTab}
              onChange={(k) => setAppTab(k as 'install' | 'update')}
            />
          </div>
        )}
        {onUpdateTab && (
          <UpdateAppPanel
            name={updateApp?.name ?? dep.update_only_app_id ?? ''}
            rules={updateApp?.requirement_rules ?? null}
          />
        )}
        {/* The four counts the reference leads with. `install_stats` is null when Intune could
            not be reached, which is NOT four zeros — rendering zeros there would say nobody has
            the app when the truth is that nobody asked Intune. */}
        {updateTabGone ? (
          <p className="mb-6 rounded-xl border border-theme-border-primary bg-theme-secondary px-4 py-3 text-sm text-theme-text-secondary">
            The update app is not in Intune, so it has no install status.
          </p>
        ) : statusPreparing ? (
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4" aria-hidden>
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-[84px] animate-pulse rounded-xl border border-theme-border-primary bg-theme-tertiary"
              />
            ))}
          </div>
        ) : statusFailed ? (
          <div role="alert" className="mb-6 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-red-500/30 bg-red-500/[0.07] px-4 py-3 text-sm text-theme-text-primary">
            <span>
              Install status could not be read from Intune.{' '}
              {deviceStatusQ.data?.error ?? errorText(deviceStatusQ.error, '')}
            </span>
            <button type="button" className={btnOutline} onClick={() => deviceStatusQ.refetch()}
              disabled={deviceStatusQ.isFetching}>
              {deviceStatusQ.isFetching ? 'Retrying…' : 'Retry'}
            </button>
          </div>
        ) : noDevices ? (
          <p className="mb-6 rounded-xl border border-theme-border-primary bg-theme-secondary px-4 py-3 text-sm text-theme-text-secondary">
            No devices yet. No device has reported an install of this app to Intune.
          </p>
        ) : installStats ? (
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard label="Installed" value={installStats.installed}
                      icon={CheckCircleIcon} tone="green" />
            <StatCard label="Pending" value={installStats.pending}
                      icon={ClockIcon} tone="amber" />
            <StatCard label="Failed" value={installStats.failed}
                      icon={XCircleIcon} tone="red" />
            <StatCard label="Not applicable" value={installStats.notApplicable}
                      icon={MinusCircleIcon} tone="slate" />
          </div>
        ) : null}

        {/* ---- App information (spec §6.1) ---------------------------------- */}
        {dep.intune_metadata && (
          <SectionCard
            title="App information"
            className="mb-6"
            collapsible
            defaultOpen={false}
            summary={[
              dep.intune_metadata.owner,
              dep.intune_metadata.categories?.length
                ? dep.intune_metadata.categories.join(', ')
                : null,
              dep.intune_metadata.featured ? 'Featured' : null,
            ].filter(Boolean).join(' · ') || 'Not set'}
            action={
              <span className="flex items-center gap-3">
                {saveAppInfo.isPending && (
                  <span
                    role="status"
                    className="flex items-center gap-1.5 text-[12px] text-theme-text-tertiary"
                  >
                    <Spinner className="h-3.5 w-3.5 shrink-0 text-theme-accent-blue" />
                    Saving to Intune…
                  </span>
                )}
                <EditAction
                  edited={edited('app_information')}
                  onClick={() => {
                    setAppInfoDraft({
                      owner: dep.intune_metadata?.owner || '',
                      developer: dep.intune_metadata?.developer || '',
                      notes: dep.intune_metadata?.notes || '',
                      information_url: dep.intune_metadata?.informationUrl || '',
                      privacy_url: dep.intune_metadata?.privacyUrl || '',
                      is_featured: !!dep.intune_metadata?.featured,
                      allow_uninstall: !!dep.allow_uninstall_from_company_portal,
                      categories: (dep.intune_metadata as any)?.categories || [],
                    })
                    setEditAppInfo(true)
                  }}
                />
              </span>
            }
          >
            {onUpdateTab && <SharedNote />}
            {/* Intune portal properties: a PATCH on the app object, so unlike the user
                experience below, changing any of this costs nothing on a device. */}
            <dl className="space-y-3 text-sm">
              <Row label="Owner" value={dep.intune_metadata.owner || '—'} />
              <Row label="Developer" value={dep.intune_metadata.developer || '—'} />
              <Row
                label="Category"
                value={
                  dep.intune_metadata.categories?.length
                    ? dep.intune_metadata.categories.join(', ')
                    : 'None'
                }
              />
              <Row label="Featured in Company Portal"
                   value={dep.intune_metadata.featured ? 'Yes' : 'No'} />
              <Row label="Uninstall in Company Portal"
                   value={dep.allow_uninstall_from_company_portal ? 'Allowed' : 'Not allowed'} />
              <Row label="Information URL" value={dep.intune_metadata.informationUrl || '—'} />
              <Row label="Privacy URL" value={dep.intune_metadata.privacyUrl || '—'} />
              <Row label="Notes" value={dep.intune_metadata.notes || '—'} />
            </dl>
          </SectionCard>
        )}

        {/* ---- Advanced (spec §6.4 program, §6.7 requirements) --------------- */}
        {dep.advanced && (
          <SectionCard
            title="Advanced"
            className="mb-6"
            collapsible
            defaultOpen={false}
            summary={[
              dep.advanced.architectures || 'any architecture',
              dep.advanced.minimumOs ? `Windows ${dep.advanced.minimumOs}` : null,
              // Extra rules, or — when there are none — what the hardware floors come to, so the
              // line says something either way rather than trailing off after the OS.
              dep.advanced.requirementRules?.length
                ? `${dep.advanced.requirementRules.length} extra rule${
                    dep.advanced.requirementRules.length === 1 ? '' : 's'}`
                : hardwareSummary({
                    ...emptyRequirements(),
                    diskSpaceMb: dep.advanced.minimumFreeDiskSpaceMB ?? null,
                    memoryMb: dep.advanced.minimumMemoryMB ?? null,
                    logicalProcessors: dep.advanced.minimumProcessors ?? null,
                    cpuSpeedMhz: dep.advanced.minimumCpuSpeedMHz ?? null,
                  }),
            ].filter(Boolean).join(' · ')}
            action={
              <EditAction
                edited={edited('advanced')}
                onClick={() => {
                  const a: any = dep.advanced || {}
                  const archs = String(a.architectures || '')
                    .split(',').map((s: string) => s.trim())
                    .filter((x: string) => ['x86', 'x64', 'arm64'].includes(x)) as Arch[]
                  setAdvancedReq({
                    architectures: archs,
                    minimumOs: a.minimumOs || emptyRequirements().minimumOs,
                    diskSpaceMb: a.minimumFreeDiskSpaceMB ?? null,
                    memoryMb: a.minimumMemoryMB ?? null,
                    logicalProcessors: a.minimumProcessors ?? null,
                    cpuSpeedMhz: a.minimumCpuSpeedMHz ?? null,
                    rules: [],
                  })
                  setAdvancedProg({
                    restartBehaviour:
                      (a.installExperience?.deviceRestartBehavior as RestartBehaviour) ||
                      DEFAULT_RESTART_BEHAVIOUR,
                    returnCodes:
                      Array.isArray(a.returnCodes) && a.returnCodes.length
                        ? a.returnCodes
                        : defaultReturnCodes(),
                  })
                  setEditAdvanced(true)
                }}
              />
            }
          >
            {onUpdateTab && (
              <SharedNote what="These settings, except the update app's requirement script, are" />
            )}
            {/* Reported as rows rather than as the editor rendered read-only: Graph's
                requirementRules do not map back into the editor's rule shape, so the editor lists
                them as read-only entries instead. */}
            <dl className="space-y-3 text-sm">
              <LineRow label="Operating system architecture">
                {formatArchitectures(dep.advanced.architectures)}
              </LineRow>
              <LineRow label="Minimum Windows release">{dep.advanced.minimumOs || '—'}</LineRow>
              <LineRow label="Install context">
                {formatSettingValue(
                  'installExperience.runAsAccount',
                  dep.advanced.installExperience?.runAsAccount,
                )}
              </LineRow>
              <LineRow label="Device restart behaviour">
                {formatSettingValue(
                  'installExperience.deviceRestartBehavior',
                  dep.advanced.installExperience?.deviceRestartBehavior,
                )}
              </LineRow>
              <LineRow label="Minimum disk space">
                {dep.advanced.minimumFreeDiskSpaceMB
                  ? `${dep.advanced.minimumFreeDiskSpaceMB} MB`
                  : '—'}
              </LineRow>
              <LineRow label="Minimum memory">
                {dep.advanced.minimumMemoryMB ? `${dep.advanced.minimumMemoryMB} MB` : '—'}
              </LineRow>
              <LineRow label="Requirement rules">
                {dep.advanced.requirementRules?.length
                  ? `${dep.advanced.requirementRules.length} on the app`
                  : 'None'}
              </LineRow>
              <LineRow label="Return codes">
                {dep.advanced.returnCodes?.length
                  ? `${dep.advanced.returnCodes.length} configured`
                  : 'Defaults'}
              </LineRow>
            </dl>
          </SectionCard>
        )}

        {/* ---- Automated updates (spec §6.5) -------------------------------- */}
        {/* Our subscription and auto-update rows are per app-per-tenant, so two rollouts of one
            package share this answer where the reference gives each its own. Said plainly in the
            card rather than papered over — a per-rollout switch that silently moves another
            rollout is worse than one that says it is shared. */}
        <SectionCard
          title="Automated updates"
          className="mb-6"
          collapsible
          defaultOpen={false}
          summary={
            isCatalogApp && sub
              ? pinned
                ? `Package pinned to ${sub.pinned_version ?? 'a version'}`
                : manualOnly
                  ? dep.sync_pending
                    ? 'A newer version is available — deploy to update'
                    : 'Updated by deploying it'
                  : follows
                    ? soakDays === 0
                      ? 'Follows new versions · publishes immediately'
                      : `Follows new versions · publishes ${plural(soakDays, 'day')} after release`
                    : 'Manual — stays on the published version'
              : namedDeployment
                ? dep.sync_pending
                  ? 'A newer version is available'
                  : 'Updated by deploying it'
              : auto
                ? auto.auto_update_enabled
                  ? 'Follows new versions'
                  : 'Manual — stays on the published version'
                : 'No update record'
          }
          action={
            isCatalogApp && sub ? (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-[12px] font-medium text-theme-accent-blue hover:underline"
                onClick={() => {
                  // The policy in force, not the override columns: a NULL column means the
                  // template's value applies, and an editor that opened on 0 instead wrote
                  // "publish immediately" over the template's wait at the first Save.
                  const seed: UpdatesDraft = {
                    update_mode: follows ? 'FOLLOW' : 'DISABLED',
                    pinned_version: '',
                    soak_days: soakDays,
                    keep_versions: keepVersions,
                    uninstall_previous: uninstallPrevious,
                  }
                  setUpdatesDraft(seed)
                  setUpdatesSeed(seed)
                  setSoakText(String(soakDays))
                  setEditUpdates(true)
                }}
              >
                <PencilIcon className="h-3 w-3" /> Edit
              </button>
            ) : undefined
          }
        >
          {onUpdateTab && <SharedNote what="The update policy is" />}
          {!isCatalogApp && namedDeployment ? (
            <>
              <p className="text-sm text-theme-text-secondary">
                A private app&apos;s automatic updates reach its first deployment only; deploy this
                deployment to publish a newer version.
              </p>
              {dep.sync_pending && (
                <p className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[12px] text-theme-text-primary">
                  A newer version is available
                  {sub?.latest_available_version ? ` (${sub.latest_available_version})` : ''}.
                </p>
              )}
            </>
          ) : isCatalogApp && sub ? (
            <>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-theme-text-primary">Follow new versions</p>
                  <p className="mt-0.5 text-[12px] text-theme-text-secondary">
                    {follows
                      ? soakDays === 0
                        ? 'Published to this deployment as soon as the catalog has a newer version.'
                        : `Published to this deployment ${plural(soakDays, 'day')} after the vendor releases it.`
                      : 'This deployment stays on the version you published. Update it yourself.'}
                    {editedTag('followNewVersions')}
                  </p>
                </div>
                <Toggle
                  checked={follows}
                  onChange={(v) => setFollowNewVersions.mutate(v)}
                  // The pin holds every deployment; the first one's switch shares its setting.
                  disabled={setFollowNewVersions.isPending || (pinned && !namedDeployment)}
                  label="Follow new versions"
                />
              </div>
              {pinned && (
                <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2.5 text-[12px] text-theme-text-primary">
                  <span>
                    The package is pinned to {sub.pinned_version ?? 'a version'}: no deployment of it
                    is updated until it is unpinned.
                  </span>
                  <button type="button" className={btnOutline} onClick={() => unpin.mutate()}
                    disabled={unpin.isPending}>
                    {unpin.isPending ? 'Unpinning…' : 'Unpin'}
                  </button>
                </div>
              )}
              {manualOnly && (
                <p className="mt-4 rounded-lg border border-theme-border-primary bg-theme-tertiary px-3 py-2 text-[12px] text-theme-text-secondary">
                  This deployment is updated when you deploy it: choose Update on it to publish
                  a newer version.
                  {dep.sync_pending && sub.latest_available_version
                    ? ` A newer version is available (${sub.latest_available_version}).`
                    : ''}
                </p>
              )}
              {dep.update_state && <UpdateState dep={dep} />}
              {!dep.update_state && !namedDeployment && sub.sync_pending && sub.latest_available_version && (
                <PendingPublish
                  version={sub.latest_available_version}
                  follows={follows}
                  soakDays={soakDays}
                  onApprove={() => sync.mutate(sub.id)}
                  approving={sync.isPending}
                />
              )}
              <dl className="mt-4 space-y-3 border-t border-theme-border-primary pt-3 text-sm">
                <LineRow label="Wait before publishing">
                  {soakDays === 0 ? 'None' : plural(soakDays, 'day')}
                  {editedTag('soakDays')}
                </LineRow>
                <LineRow label="Versions kept">
                  {keepVersions === 0 ? 'Only the latest' : `${keepVersions} previous`}
                  {editedTag('keepVersions')}
                </LineRow>
                <LineRow label="Supersedence">
                  {!supersede
                    ? 'None'
                    : uninstallPrevious
                      ? 'Replaces the previous version'
                      : 'Updates the previous version in place'}
                  {editedTag('uninstallPrevious')}
                </LineRow>
                {/* dep.version is the app object's own displayVersion, read live from Intune;
                    the subscription's synced version is our bookkeeping of the same fact. */}
                <LineRow label="Deployed version">
                  {dep.version ?? sub.current_synced_version ?? '—'}
                </LineRow>
                <LineRow label="Latest available">{sub.latest_available_version ?? '—'}</LineRow>
                <LineRow label="Last sync">
                  {sub.last_sync_at ? dateTime(sub.last_sync_at) : '—'}
                </LineRow>
              </dl>
            </>
          ) : auto ? (
            <dl className="space-y-3 text-sm">
              <LineRow label="Auto-update">{auto.auto_update_enabled ? 'On' : 'Off'}</LineRow>
              <LineRow label="Status">{auto.update_status}</LineRow>
              <LineRow label="Latest source version">{auto.latest_source_version ?? '—'}</LineRow>
              <LineRow label="Package source">{auto.package_source ?? '—'}</LineRow>
              <LineRow label="Last checked">
                {auto.last_update_check ? dateTime(auto.last_update_check) : '—'}
              </LineRow>
            </dl>
          ) : (
            <p className="text-sm text-theme-text-tertiary">
              No subscription or auto-update record for this app.
            </p>
          )}

          {/* Every publish of this package, whether or not it has an update policy — the history
              is a fact about the app, not about how it is kept current. */}
          {!!dep.versions?.length && <VersionHistory versions={dep.versions} />}

          {isCatalogApp && sub && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-theme-border-primary bg-theme-tertiary px-3 py-2 text-[12px] text-theme-text-secondary">
            <span>
              These settings belong to <strong>this deployment</strong> only; the package&apos;s
              other deployments keep their own. Retention runs at the next publish, on whatever
              falls outside the window; changing the number never acts on what is already in
              Intune.
            </span>
            {policyEdited.size > 0 && (
              <button type="button" className={btnOutline} onClick={() => resetUpdates.mutate()}
                disabled={resetUpdates.isPending}>
                Use the template&apos;s values
              </button>
            )}
          </div>
          )}
        </SectionCard>

        {/* ---- User experience ---------------------------------------------- */}
        {dep.deployment_config && (
          <SectionCard
            title="User experience"
            className="mb-6"
            collapsible
            defaultOpen={false}
            summary={uxLine(dep.deployment_config_source ?? dep.deployment_config)}
            action={
              <EditAction
                edited={edited('experience')}
                kept={KEPT_WITH_UX_TEMPLATE}
                onClick={() => setEditExperience(true)}
              />
            }
          >
            {onUpdateTab && <SharedNote what="The user experience is" />}
            <dl className="space-y-3 text-sm">
              {/* Which template this rollout came from, and whether its settings were adjusted for
                  this rollout only. From the publish audit entry: an adjusted delivery is
                  deliberately never written back to the template (spec §7a.1), so the app object
                  cannot answer it and re-reading the template would answer a different question. */}
              <LineRow label="Template">
                <span className="flex flex-wrap items-center justify-end gap-1.5">
                  {dep.ux_template?.adjusted && <Badge tone="amber">Adjusted</Badge>}
                  {dep.ux_template?.name || 'Not recorded'}
                </span>
              </LineRow>
              <LineRow label="Behaviour">
                {uxLine(dep.deployment_config_source ?? dep.deployment_config)}
              </LineRow>
              <LineRow label="Settings delivered">
                {formatSettingValue('config_transport', dep.config_transport)}
              </LineRow>
            </dl>
            {/* spec §7a.2: this is the one card whose change is invisible on a device that
                already installed. Worth saying plainly rather than leaving someone to wonder
                why the new behaviour has not appeared. */}
            <p className="mt-3 rounded-lg border border-theme-border-primary bg-theme-tertiary px-3 py-2 text-[12px] text-theme-text-secondary">
              These ride on the app's install command, not inside the package — changing them
              updates the Intune app object with no content re-upload. Devices that already
              installed keep the behaviour they installed with until the next install or repair.
            </p>
          </SectionCard>
        )}

        {/* ---- who gets it ------------------------------------------------- */}
        {/* Read-only here, edited in the full assignment editor. The blade renders the live set
            disabled rather than a hand-rolled list, so what is reported and what is edited cannot
            describe the same assignment differently. */}
        <SectionCard
          title="Assignment"
          className="mb-6"
          collapsible
          defaultOpen={false}
          summary={
            assignTab === 'updateOnly'
              ? updateOnlyRows.length
                ? assignmentSummary(updateOnlyRows, updateOnlyGroups)
                : 'Update-only app not assigned'
              : appRows.length
                ? assignmentSummary(appRows, appGroups)
                : 'Not assigned'
          }
          action={
            updateTabGone ? undefined
              : <EditAction edited={edited('assignments')} onClick={() => setAssignOpen(assignTab)} />
          }
        >
          {/* The update-only app is a separate Intune app with its own assignments: assigning
              the install app does not assign it. The app tab above picks which one is shown. */}
          {assignTab === 'updateOnly' && (
            <p className="mb-3 rounded-lg border border-theme-border-primary bg-theme-tertiary px-3 py-2 text-[12px] text-theme-text-secondary">
              Published as{' '}
              <span className="font-medium text-theme-text-primary">
                {updateApp?.name ?? dep.update_only_app_id}
              </span>
              . It patches devices that already have the app and installs nothing new, so a broad
              target is the recommendation here rather than the risk it would be on the install app.
            </p>
          )}
          {(assignTab === 'updateOnly' ? updateOnlyQ.isLoading : assignQ.isLoading) ? (
            <p className="py-4 text-sm text-theme-text-tertiary">Loading…</p>
          ) : (assignTab === 'updateOnly' ? updateOnlyRows : appRows).length === 0 ? (
            <EmptyState
              title={assignTab === 'updateOnly' ? 'Update-only app not assigned' : 'Not assigned'}
              hint={
                assignTab === 'updateOnly'
                  ? 'The update-only app patches nothing until it has an assignment of its own.'
                  : 'Nobody receives this app until it has at least one assignment.'
              }
            />
          ) : (
            <AssignmentBlade
              value={assignTab === 'updateOnly' ? updateOnlyRows : appRows}
              onChange={() => {}}
              groups={assignTab === 'updateOnly' ? updateOnlyGroups : appGroups}
              /* The update-only app only ever patches what is already installed, so Available and
                 Uninstall are not intents it can carry. */
              intents={assignTab === 'updateOnly' ? ['required'] : appIntents}
              disabled
            />
          )}
        </SectionCard>

        {/* ---- how it is landing ------------------------------------------- */}
        {/* The per-device rows the four counts above summarise, from the same report export — a
            table built from a second source could disagree with its own totals. */}
        <h3 className="mb-3 text-sm font-semibold text-theme-text-primary">Device install status</h3>
        <div className="mb-6">
          {updateTabGone ? (
            <p className="text-sm text-theme-text-secondary">
              The update app is not in Intune, so no device reports an install of it.
            </p>
          ) : statusPreparing ? (
            <div className="flex items-center gap-2.5 rounded-lg border border-theme-border-primary bg-theme-tertiary px-4 py-3 text-sm text-theme-text-secondary">
              <Spinner className="h-4 w-4 shrink-0 text-theme-accent-blue" />
              Reading per-device install status from Intune. Intune prepares it as a report, which
              can take a minute; this page checks again every few seconds.
            </div>
          ) : (
            <DeviceInstallStatusTable
              statuses={deviceStatusQ.data?.device_statuses ?? null}
              unavailableNote={
                'Intune could not be asked for per-device status. That is not the same as no ' +
                'devices — the counts above are missing for the same reason.'
              }
            />
          )}
        </div>

        {/* ---- everything else, folded away ---------------------------------
            The reference has no card for any of this, and it is all reference material: what the
            commands came out as, the parameters they carry, and the facts about the rollout that
            the cards above already say in words. It sits last and closed so the page ends on the
            device table rather than on a wall of detail.

            Read-only. The commands are GENERATED — the user experience, the advanced settings and
            the template resolve into them — so the way to change one is to change the thing that
            produces it, in the cards above. An editor here could put the app out of step with every
            card that claims to describe it. */}
        <SectionCard
          title="More"
          collapsible
          defaultOpen={false}
          summary={[
            dep.version && `v${dep.version}`,
            dep.architecture,
            dep.install_context,
            dep.drift?.comparable && dep.drift.drifted ? 'changed in Intune' : null,
          ].filter(Boolean).join(' · ') || 'Commands, parameters and rollout facts'}
        >
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
            <Row label="Publisher" value={dep.publisher} />
            <Row label="Version" value={dep.version} />
            <Row label="Target" value={dep.target_details || dep.target_type} />
            <Row label="Architecture" value={dep.architecture} />
            <Row label="Install context" value={dep.install_context} />
            <Row label="Packaging mode" value={dep.packaging_mode} />
            <Row label="Checksum mode" value={dep.checksum_mode} />
            <Row label="Devices" value={dep.device_count != null ? String(dep.device_count) : undefined} />
            <Row label="Deployed by" value={dep.deployed_by} />
            <Row
              label="Last updated"
              value={dep.last_updated ? dateTime(dep.last_updated) : undefined}
            />
            <Row label="Intune app id"
                 value={(onUpdateTab ? dep.update_only_app_id : dep.intune_app_id) ?? undefined} />
          </dl>
          {dep.in_catalog && dep.app_id && (
            <p className="mt-3 text-[12px] text-theme-text-tertiary">
              {/* Download lives on the catalog page on purpose: the file this endpoint can serve is
                  the package's CURRENT build, not the one this rollout shipped. */}
              <Link to={`/apps/${dep.app_id}`} className="text-theme-accent-blue hover:underline">
                View the package in the catalog
              </Link>{' '}
              to download it or see its other deployments.
            </p>
          )}

          {/* The -DeploymentConfig sidecar is hundreds of characters of base64, so the command is
              shown with its payload elided to the ends — the executable, the verb and the flags
              are the readable part. Show opens it whole; copy carries it verbatim. */}
          <div className="mt-4 border-t border-theme-border-primary pt-3">
            <p className="mb-1 text-[12px] text-theme-text-tertiary">Install</p>
            {dep.install_command ? (
              <CommandBlock command={dep.install_command} copyLabel="Copy install command" />
            ) : (
              <p className="text-[12px] text-theme-text-primary">—</p>
            )}
            <p className="mb-1 mt-3 text-[12px] text-theme-text-tertiary">Uninstall</p>
            {dep.uninstall_command ? (
              <CommandBlock command={dep.uninstall_command} copyLabel="Copy uninstall command" />
            ) : (
              <p className="text-[12px] text-theme-text-primary">—</p>
            )}
          </div>

          {/* The PARAMETERS, not just the command. Everything the user experience is made of rides
              in the -DeploymentConfig sidecar as base64, so the command line above is exact and
              completely unreadable — this is the half that tells you what the deployment does.

              Read back from Graph and decoded, NOT re-resolved from the template: a template edited
              after this deployment shipped would otherwise make the page misreport its own history. */}
          {dep.deployment_config && (
            <div className="mt-4 border-t border-theme-border-primary pt-3">
              <p className="mb-2 text-[12px] text-theme-text-tertiary">
                Deployment parameters{' '}
                <span className="text-theme-text-tertiary">
                  — decoded from -DeploymentConfig, as published
                </span>
              </p>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
                {flattenConfig(dep.deployment_config).map(([k, v]) => (
                  <div key={k} className="flex items-baseline justify-between gap-3 border-b border-theme-border-primary py-1 last:border-0">
                    <dt className="shrink-0 font-mono text-[11px] text-theme-text-tertiary">{k}</dt>
                    <dd className="text-right text-[12px] font-medium text-theme-text-primary">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
          {dep.config_note && (
            <p className="mt-2 text-[12px] text-amber-400">{dep.config_note}</p>
          )}

          {/* Changed in Intune after we published it. Worth calling out for two reasons: the command
              above is then someone else's edit presented as ours, and the next re-publish will revert
              it — which is inexplicable unless you knew it had been edited.

              Only shown when there IS a baseline. `comparable: false` means we have nothing to
              compare against, and a "changed" badge on every app AEAM never published would be
              worse than saying nothing. */}
          {dep.drift?.comparable && dep.drift.drifted && (
            <div className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/[0.06] p-3">
              <p className="text-[12px] font-medium text-amber-300">
                Changed in Intune after this deployment was published
              </p>
              <p className="mt-0.5 text-[12px] text-theme-text-tertiary">
                {dep.drift.changes.length} difference
                {dep.drift.changes.length === 1 ? '' : 's'} from what Agentic Packr published. Re-publishing
                this app will replace these with the template's values.
              </p>
              <dl className="mt-2 space-y-1">
                {dep.drift.changes.map((c) => (
                  <div key={c.field} className="border-t border-amber-500/20 pt-1">
                    <dt className="font-mono text-[11px] text-theme-text-tertiary">{c.field}</dt>
                    <dd className="text-[12px] text-theme-text-primary">
                      <span className="text-theme-text-tertiary">published:</span>{' '}
                      <span className="font-mono">{String(c.ours)}</span>
                      {' → '}
                      <span className="text-theme-text-tertiary">now:</span>{' '}
                      <span className="font-mono text-amber-300">{String(c.theirs)}</span>
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
          {dep.drift && !dep.drift.comparable && dep.drift.note && (
            <p className="mt-2 text-[12px] text-theme-text-tertiary">{dep.drift.note}</p>
          )}
        </SectionCard>
      </div>

      {assignOpen && dep.intune_app_id && (
        <AssignmentModal
          isOpen={!!assignOpen}
          onClose={() => setAssignOpen(null)}
          deploymentId={dep.intune_app_id}
          appName={assignOpen === 'updateOnly' ? updateApp?.name || dep.app_name : dep.app_name}
          updateOnlyAppId={dep.update_only_app_id || undefined}
          scope={assignOpen}
          appIntents={appIntents}
          onSuccess={() => {
            clearMessages()
            setNotice('Assignments saved in Intune.')
            applySaved((d) => ({
              ...d,
              overrides: { ...(d.overrides ?? {}), assignments: d.overrides?.assignments ?? {} },
            }))
            qc.invalidateQueries({ queryKey: intuneKeys.assignments(deploymentId) })
            qc.invalidateQueries({ queryKey: intuneKeys.assignments(updateOnlyId) })
            qc.invalidateQueries({ queryKey: intuneKeys.deployments() })
          }}
        />
      )}

      <Modal
        open={editAppInfo}
        onClose={() => !saveAppInfo.isPending && setEditAppInfo(false)}
        title="Edit app information"
        footer={
          <>
            <button
              type="button"
              className={btnOutline}
              onClick={() => setEditAppInfo(false)}
              disabled={saveAppInfo.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-lg bg-theme-accent-blue px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
              onClick={() => saveAppInfo.mutate(appInfoDraft)}
              disabled={saveAppInfo.isPending}
            >
              {saveAppInfo.isPending ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        <p className="mb-3 text-sm text-theme-text-secondary">
          Company Portal properties on the Intune app object. {KEPT_WITH_DEPLOYMENT_TEMPLATE}
          {hasSiblingApp ? ` ${BOTH_APPS}` : ''}
        </p>
        <div className="space-y-3 text-sm">
          {([
            ['owner', 'Owner'],
            ['developer', 'Developer'],
            ['information_url', 'Information URL'],
            ['privacy_url', 'Privacy URL'],
          ] as const).map(([key, label]) => (
            <label key={key} className="block">
              <span className="mb-1 block text-[12px] text-theme-text-tertiary">{label}</span>
              <input
                value={appInfoDraft[key]}
                onChange={(e) => setAppInfoDraft((d) => ({ ...d, [key]: e.target.value }))}
                className="w-full rounded-lg border border-theme-border-primary bg-theme-input px-3 py-2 text-[13px] text-theme-text-primary"
              />
            </label>
          ))}
          <label className="block">
            <span className="mb-1 block text-[12px] text-theme-text-tertiary">Notes</span>
            <textarea
              value={appInfoDraft.notes}
              onChange={(e) => setAppInfoDraft((d) => ({ ...d, notes: e.target.value }))}
              rows={3}
              className="w-full rounded-lg border border-theme-border-primary bg-theme-input px-3 py-2 text-[13px] text-theme-text-primary"
            />
          </label>
          <div>
            <span className="mb-1 block text-[12px] text-theme-text-tertiary">Category</span>
            {(() => {
              const tenantNames = catsQ.data?.categories.map((c) => c.name) ?? []
              const options = [
                ...tenantNames.map((name) => ({ name, inTenant: true })),
                ...appInfoDraft.categories
                  .filter((c) => !tenantNames.includes(c))
                  .map((name) => ({ name, inTenant: false })),
              ]
              if (catsQ.isLoading) {
                return <p className="text-[12px] text-theme-text-tertiary">Reading this tenant's categories…</p>
              }
              if (options.length === 0) {
                return <p className="text-[12px] text-theme-text-tertiary">This tenant has no app categories in Intune.</p>
              }
              return (
                <div className="flex flex-wrap gap-1.5">
                  {options.map(({ name, inTenant }) => {
                    const on = appInfoDraft.categories.includes(name)
                    return (
                      <button
                        key={name}
                        type="button"
                        title={inTenant ? undefined : 'Not a category in this Intune tenant — it will not be applied.'}
                        onClick={() =>
                          setAppInfoDraft((d) => ({
                            ...d,
                            categories: on
                              ? d.categories.filter((x) => x !== name)
                              : [...d.categories, name],
                          }))
                        }
                        className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
                          !inTenant
                            ? 'border-amber-500/60 bg-amber-500/10 text-amber-500 line-through'
                            : on
                              ? 'border-blue-500 bg-blue-500/15 text-blue-400'
                              : 'border-theme-border-primary bg-theme-secondary text-theme-text-tertiary hover:text-theme-text-primary'
                        }`}
                      >
                        {name}
                      </button>
                    )
                  })}
                </div>
              )
            })()}
          </div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={appInfoDraft.is_featured}
              onChange={(e) => setAppInfoDraft((d) => ({ ...d, is_featured: e.target.checked }))}
            />
            <span>Featured in Company Portal</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={appInfoDraft.allow_uninstall}
              onChange={(e) => setAppInfoDraft((d) => ({ ...d, allow_uninstall: e.target.checked }))}
            />
            <span>Allow uninstall from Company Portal</span>
          </label>
        </div>
      </Modal>

      {/* The mock's Advanced edit opens the full AdvancedSettings editor (architecture, minimum
          OS/hardware, requirement rules, restart behaviour, return codes) as a modal over the page,
          not a full-screen takeover. Seeded from dep.advanced; save maps to
          PATCH /deployments/{id}/advanced. */}
      <Modal
        open={editAdvanced}
        onClose={() => !saveAdvanced.isPending && setEditAdvanced(false)}
        title="Advanced settings"
        footer={
          <>
            <button
              type="button"
              className={btnOutline}
              onClick={() => setEditAdvanced(false)}
              disabled={saveAdvanced.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-lg bg-theme-accent-blue px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
              onClick={() => saveAdvanced.mutate()}
              disabled={saveAdvanced.isPending}
            >
              {saveAdvanced.isPending ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        <p className="mb-3 text-sm text-theme-text-secondary">
          {KEPT_WITH_DEPLOYMENT_TEMPLATE}
          {hasSiblingApp
            ? ` ${BOTH_APPS} The update-only app keeps its "app is installed" requirement rule.`
            : ''}
        </p>
        <AdvancedSettings
          value={advancedReq}
          onChange={setAdvancedReq}
          program={advancedProg}
          onProgramChange={setAdvancedProg}
          existingRules={dep.advanced?.requirementRules ?? []}
        />
      </Modal>

      {/* The mock's UX edit opens the full UX-template adjustment editor as a modal over the page,
          not a full-screen takeover: host the same wizard in APPLY mode inside Modal size="full"
          (the width the side-by-side PSADT preview needs), exactly as the deploy wizard does. Seeded
          from this deployment's decoded sidecar; on apply the whole config is re-rendered into the
          install/uninstall sidecar (PATCH /deployments/{id}/experience). */}
      <Modal
        open={editExperience}
        onClose={() => !saveExperience.isPending && setEditExperience(false)}
        title="Edit user experience"
        size="full"
      >
        <p className="mb-2 text-[13px] text-theme-text-tertiary">
          Changes apply to this deployment only. {KEPT_WITH_UX_TEMPLATE}
        </p>
        {hasSiblingApp && (
          <p className="mb-4 text-[13px] text-theme-text-secondary">{BOTH_APPS}</p>
        )}
        <UxTemplateWizard
          template={
            {
              // The nested authoring shape the wizard reads (identity.*, closeApps.*, defer.*),
              // reconstructed from the shipped sidecar by the backend. Seeding from the flat
              // `deployment_config` instead leaves every lookup undefined and the editor opens blank.
              config_json: dep.deployment_config_source ?? dep.deployment_config ?? {},
              // The template this rollout came from, so the editor's header names the thing being
              // adjusted. Editing here never writes back to it (spec §7a.1).
              name: dep.ux_template?.name || 'Deployment experience',
              description: '',
            } as unknown as DeploymentTemplate
          }
          onClose={() => setEditExperience(false)}
          onSaved={() => {}}
          onApply={(config) => saveExperience.mutate(config)}
          applying={saveExperience.isPending}
          sampleApp={previewSampleApp(dep)}
        />
      </Modal>

      <Modal
        open={editUpdates}
        onClose={() => !saveUpdates.isPending && setEditUpdates(false)}
        title="Edit update policy"
        footer={
          <>
            <button
              type="button"
              className={btnOutline}
              onClick={() => setEditUpdates(false)}
              disabled={saveUpdates.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-lg bg-theme-accent-blue px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
              onClick={() => saveUpdates.mutate()}
              disabled={
                saveUpdates.isPending ||
                (updatesDraft.update_mode === 'FOLLOW' && soakDaysError(soakText) !== null)
              }
            >
              {saveUpdates.isPending ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        <p className="mb-3 text-sm text-theme-text-secondary">
          Changes apply to <strong>this deployment</strong> only
          {dep?.deployment_name ? ` (${dep.deployment_name})` : ''}. They are kept when it is
          deployed again with the same deployment template.
        </p>
        <div className="space-y-4 text-sm">
          <label className="block">
            <span className="mb-1 block text-[12px] text-theme-text-tertiary">Update mode</span>
            <select
              value={updatesDraft.update_mode}
              disabled={pinned && !namedDeployment}
              onChange={(e) =>
                setUpdatesDraft((d) => ({ ...d, update_mode: e.target.value as UpdateMode }))
              }
              className="w-full rounded-lg border border-theme-border-primary bg-theme-input px-3 py-2 text-[13px] text-theme-text-primary"
            >
              <option value="FOLLOW">Follow new versions</option>
              <option value="DISABLED">Don't auto-update</option>
            </select>
          </label>
          {updatesDraft.update_mode === 'FOLLOW' && (
            <>
              <div>
                <span className="mb-1 block text-[12px] text-theme-text-tertiary">Wait before publishing</span>
                <SoakDaysInput
                  text={soakText}
                  onChange={(text) => {
                    setSoakText(text)
                    const n = soakDaysValue(text)
                    if (n !== null) setUpdatesDraft((d) => ({ ...d, soak_days: n }))
                  }}
                  inputClassName="rounded-lg border border-theme-border-primary bg-theme-input px-3 py-2 text-[13px] text-theme-text-primary"
                />
                {soakDaysError(soakText) && (
                  <p className="mt-1 text-xs text-red-400">{soakDaysError(soakText)}</p>
                )}
              </div>
              <label className="block">
                <span className="mb-1 block text-[12px] text-theme-text-tertiary">Versions to keep in Intune</span>
                <select
                  value={updatesDraft.keep_versions}
                  onChange={(e) => setUpdatesDraft((d) => ({ ...d, keep_versions: Number(e.target.value) }))}
                  className="w-full rounded-lg border border-theme-border-primary bg-theme-input px-3 py-2 text-[13px] text-theme-text-primary"
                >
                  {keepOptionsIncluding(updatesDraft.keep_versions).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-[12px] text-theme-text-tertiary">When the new version installs</span>
                <select
                  value={updatesDraft.uninstall_previous ? 'uninstall' : 'inplace'}
                  onChange={(e) =>
                    setUpdatesDraft((d) => ({ ...d, uninstall_previous: e.target.value === 'uninstall' }))
                  }
                  className="w-full rounded-lg border border-theme-border-primary bg-theme-input px-3 py-2 text-[13px] text-theme-text-primary"
                >
                  <option value="inplace">Let the installer update it (recommended)</option>
                  <option value="uninstall">Uninstall it first</option>
                </select>
              </label>
            </>
          )}
        </div>
      </Modal>

      <Modal
        open={confirmRedeploy}
        onClose={() => setConfirmRedeploy(false)}
        title="Redeploy to Intune"
        footer={
          <>
            <button type="button" className={btnOutline} onClick={() => setConfirmRedeploy(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="rounded-lg bg-theme-accent-blue px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
              onClick={() => {
                setConfirmRedeploy(false)
                redeploy.mutate()
              }}
            >
              Redeploy
            </button>
          </>
        }
      >
        <p className="text-sm text-theme-text-secondary">
          Publishes the install and uninstall commands and the detection rule of the current
          package to{' '}
          <strong className="text-theme-text-primary">
            {(dep.apps ?? []).map((a) => a.name).filter(Boolean).join(' and ') || dep.app_name}
          </strong>
          . Nothing is uploaded again.
        </p>
        <p className="mt-2 text-sm text-theme-text-secondary">
          The names, assignments, user experience and update policy stay as they are.
          {hasSiblingApp ? ' Both apps of this deployment get the same commands.' : ''}
        </p>
      </Modal>

      <Modal
        open={confirmRemove}
        onClose={() => {
          if (remove.isPending) return
          // The app this page describes is already deleted; only the list is left to show.
          if (removeIncomplete) navigate('/deployments')
          else setConfirmRemove(false)
        }}
        title="Remove deployment"
        footer={
          removeIncomplete ? (
            <button type="button" className={btnOutline} onClick={() => navigate('/deployments')}>
              Back to deployments
            </button>
          ) : (
            <>
              <button
                type="button"
                className={btnOutline}
                onClick={() => setConfirmRemove(false)}
                disabled={remove.isPending}
              >
                Cancel
              </button>
              {/* Disabled until the preview has loaded: the confirmation has to list every app
                  that will be deleted before it can be given. Stays disabled while another app
                  depends on one of them, because Intune refuses that delete. */}
              <button
                type="button"
                className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                onClick={() => remove.mutate()}
                disabled={remove.isPending || !removal || removalBlocked(removalPreview.preview?.deployments)}
              >
                {remove.isPending ? 'Removing…' : 'Remove'}
              </button>
            </>
          )
        }
      >
        {removeIncomplete ? (
          <p className="text-sm text-red-700 dark:text-red-400">{removeIncomplete}</p>
        ) : (
          <>
            <RemoveScopeChoice
              shown={dep.role !== 'update_only' && !!dep.update_only_app_id}
              scope={removeScope}
              onChange={setRemoveScope}
              disabled={remove.isPending}
            />
            {removal?.role === 'update_only' ? (
              <>
                <p className="text-sm text-theme-text-secondary">
                  Remove the update-only app of the deployment{' '}
                  <strong className="text-theme-text-primary">{removeTitle}</strong>? Devices that
                  already have it are no longer updated by Intune. The install app and its
                  assignments are not changed.
                  {removal.related.length > 0 ? ' Deleted with it:' : ''}
                </p>
                <p className="mt-1 text-xs text-theme-text-tertiary">
                  Intune app: {removal.app_name || dep.app_name}
                </p>
                <RelatedAppList related={removal.related} />
                <RelationshipNotes item={removal} />
                <GroupsNote items={[removal]} />
              </>
            ) : removal ? (
              <>
                <p className="text-sm text-theme-text-secondary">
                  {removesInstallAppOnly ? 'Remove the install app of the deployment' : 'Remove the deployment'}{' '}
                  <strong className="text-theme-text-primary">{removeTitle}</strong>? The app and
                  its assignments are deleted from Intune
                  {removal.related.length > 0 ? ', together with:' : '.'}
                </p>
                <p className="mt-1 text-xs text-theme-text-tertiary">
                  Intune app: {removal.app_name || dep.app_name}
                </p>
                <RelatedAppList related={removal.related} />
                <RelationshipNotes item={removal} />
                <p className="text-sm text-theme-text-secondary">
                  Nothing is uninstalled from devices, but Intune will no longer install, update
                  or uninstall this app. The package stays in the catalog.
                </p>
                <GroupsNote items={[removal]} />
              </>
            ) : null}
            <RemovalPreviewStatus query={removalPreview.query} />
          </>
        )}
      </Modal>
    </div>
  )
}
